import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EntitlementService } from '@shared-api/entitlement/entitlement.service';
import {
  EntitlementsConfig,
  EntitlmentResponse,
} from '@shared-interfaces/shared-entitlements';
import { Subscription } from '@shared-interfaces/shared-subscription';
import { ConfigurationService } from '@shared-utils/configuration/configuration.service';
import { SubscriptionService } from '@shared-utils/subscription/subscription.service';
import { EMPTY, Observable, Subject, catchError, switchMap } from 'rxjs';
import { StateService } from '../../../shared-state/state.service';
import { Settings } from '@shared-interfaces/shared-settings';
import { TranslateService } from '@ngx-translate/core';

export type SubIdSelectorStatus =
  | 'pending'
  | 'loading'
  | 'updating'
  | 'configured'
  | 'subscriptions'
  | 'success'
  | 'error';

interface SubIdSelectorState {
  status: SubIdSelectorStatus;
  entitlementsConfig: EntitlementsConfig | undefined;
  subscriptions: Subscription[] | undefined;
  entitlements: EntitlmentResponse[] | undefined;
  tempSettings: Settings | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class SubIdSelectorService {
  private configService = inject(ConfigurationService);
  private entitlementsService = inject(EntitlementService);
  private subscriptionService = inject(SubscriptionService);
  private translateService = inject(TranslateService);

  // sources && actions
  getEntitlementConfig$ = new Subject<void>();
  private entitlementsConfig$ = this.getEntitlementConfig$.pipe(
    switchMap(() =>
      this.entitlementsConfig()
        ? EMPTY
        : this.getEntitlementsConfig().pipe(
            catchError((err) => {
              console.error(err);
              this.error$.next(err);
              return EMPTY;
            }),
          ),
    ),
  );

  getSubscriptions$ = new Subject<void>();
  private initSubIdFromQueryParams$ = this.getSubscriptions$.pipe(
    switchMap(() =>
      this.subscriptions()
        ? EMPTY
        : this.loadAndHandleSubscriptions().pipe(
            catchError((err) => {
              console.error(err);
              this.error$.next(err);
              return EMPTY;
            }),
          ),
    ),
  );

  getEntitlements$ = new Subject<void>();
  private entitlementsAndTeams$ = this.getEntitlements$.pipe(
    switchMap(() => {
      return !this.entitlements()
        ? this.entitlementsService
            .getEntitlements(this.subscriptions()?.[0]?.subscription_id ?? '')
            .pipe(
              catchError((err) => {
                console.error('Error fetching entitlements:', err);
                this.error$.next(err);
                return EMPTY;
              }),
            )
        : EMPTY;
    }),
  );

  updateEntitlements$ = new Subject<Settings>();
  private updatedEntitlements$ = this.updateEntitlements$.pipe(
    switchMap((settings: Settings) =>
      this.entitlementsService.updateEntitlements(settings).pipe(
        catchError((err) => {
          console.error(err);
          this.error$.next(err);
          return EMPTY;
        }),
      ),
    ),
  );

  private error$ = new Subject<any>();

  // state
  private componentState = signal<SubIdSelectorState>({
    status: 'pending',
    entitlementsConfig: undefined,
    subscriptions: undefined,
    entitlements: undefined,
    tempSettings: undefined,
  });

  // selectors
  entitlementsConfig = computed(() => this.componentState().entitlementsConfig);
  subscriptions = computed(() => this.componentState().subscriptions);
  entitlements = computed(() => this.componentState().entitlements);
  tempSettings = computed(() => this.componentState().tempSettings);
  status = computed(() => this.componentState().status);

  constructor() {
    // reducers
    this.entitlementsConfig$.pipe(takeUntilDestroyed()).subscribe({
      next: (entitlementsConfig: EntitlementsConfig) => {
        this.componentState.update((componentState) => ({
          ...componentState,
          status: 'configured',
          entitlementsConfig: entitlementsConfig,
        }));

        // dispatch the next action to get subscriptions
        this.getSubscriptions$.next();
      },
    });

    this.initSubIdFromQueryParams$
      .pipe(takeUntilDestroyed())
      .subscribe((subscriptions: Subscription[]) => {
        this.componentState.update((componentState) => ({
          ...componentState,
          status: 'subscriptions',
          subscriptions: subscriptions.sort((a, b) =>
            a.serviceNickname > b.serviceNickname ? 1 : -1,
          ),
        }));

        // dispatch the next action to get entitlements
        this.getEntitlements$.next();
      });

    this.entitlementsAndTeams$
      .pipe(takeUntilDestroyed())
      .subscribe((entitlements: EntitlmentResponse[]) => {
        this.componentState.update((componentState) => ({
          ...componentState,
          status: 'success',
          entitlements: entitlements,
        }));
      });

    this.updateEntitlements$
      .pipe(takeUntilDestroyed())
      .subscribe((settings: Settings) =>
        this.componentState.update((componentState) => ({
          ...componentState,
          status: 'updating',
          tempSettings: settings,
        })),
      );

    this.updatedEntitlements$.pipe(takeUntilDestroyed()).subscribe(() => {
      // update operation from tempSettings
      const indexToUpdate = this.entitlements()?.findIndex(
        (x) => x.subscriptionId === this.tempSettings()?.subscriptionId,
      );
      var updatedEntitlements = this.entitlements();
      updatedEntitlements![indexToUpdate!].serviceNickname =
        this.tempSettings()?.serviceNickname!;

      this.componentState.update((componentState) => ({
        ...componentState,
        status: 'success',
        entitlements: updatedEntitlements,
        tempSettings: undefined,
      }));
    });

    this.getEntitlementConfig$.pipe(takeUntilDestroyed()).subscribe(() =>
      this.componentState.update((componentState) => ({
        ...componentState,
        status: 'loading',
      })),
    );

    this.error$.pipe(takeUntilDestroyed()).subscribe(() =>
      this.componentState.update((componentState) => ({
        ...componentState,
        status: 'error',
      })),
    );
  }

  private getEntitlementsConfig(): Observable<EntitlementsConfig> {
    return new Observable<EntitlementsConfig>((observer) => {
      const translatedConfig: EntitlementsConfig = {
        entitlementsTitle: this.translateService.instant('entitlements.entitlementsTitle'),
        entitlementsPunchLine: this.translateService.instant('entitlements.entitlementsPunchLine'),
        entitlementsDescription: this.translateService.instant('entitlements.entitlementsDescription'),
      };
      observer.next(translatedConfig);
      observer.complete();
    });
  }

  private loadAndHandleSubscriptions(): Observable<Subscription[]> {
    return this.subscriptionService.getSubscriptionIds();
  }
}
